/**
 * StickyHeroStrip — A thin, compact, progressively-revealed strip
 * that appears at the top of the viewport when the user has
 * scrolled past the full hero. Mirrors the page's section colour
 * so navigation context is preserved while reclaiming screen
 * real-estate for the body content.
 *
 * Usage: passed implicitly by `PageShell` when it has a `hero`
 * and an optional `stickyTitle` / `stickyActions`. A page can
 * skip the sticky strip by passing `stickyDisabled` to PageShell.
 */
import React, { useEffect, useState } from 'react';
import { cn } from '../../lib/utils';

export function StickyHeroStrip({
  title,
  actions,
  theme,
  triggerRef,
  testId = 'sticky-hero-strip',
}) {
  const [visible, setVisible] = useState(false);

  // We poll the sentinel's bounding rect on scroll. IntersectionObserver
  // would be more elegant, but on pages with deeply nested scroll
  // containers (or any ancestor that sets `overflow: hidden`) it
  // silently fails to fire. A scroll listener with rAF throttling
  // is rock-solid across every page in the app and the perf cost
  // is negligible — we read one bounding rect on each rAF tick
  // while the user is actually scrolling.
  useEffect(() => {
    let frame = null;
    const update = () => {
      frame = null;
      const sentinel = triggerRef?.current;
      if (!sentinel) return;
      const rect = sentinel.getBoundingClientRect();
      // Sentinel sits at the bottom of the hero. When its top
      // edge has scrolled above the viewport (negative y) the
      // hero is gone and the strip should appear.
      setVisible(rect.top < 0);
    };
    const onScroll = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [triggerRef]);

  if (!title && !actions) return null;

  // A light hero (slate-50 / white gradient) needs dark sticky text;
  // saturated/dark heroes keep white text.
  const heroBg = String(theme?.heroGradient || '');
  const isLightHero = heroBg.includes('slate-50') || heroBg.includes('white') || heroBg.includes('slate-100');

  return (
    <div
      className={cn(
        // Fixed positioning so the strip never occupies block-flow
        // space — without this, an opacity-0 / -translate-y-full
        // sticky element still reserves its own height and creates
        // a visible whitespace gap above the hero.
        'fixed top-16 right-0 z-30 transition-all duration-200 ease-out',
        visible
          ? 'opacity-100 translate-y-0 pointer-events-auto'
          : 'opacity-0 -translate-y-full pointer-events-none',
        theme?.heroGradient || 'bg-slate-900',
        isLightHero ? 'text-slate-900 border-b border-slate-200' : 'text-white',
        'shadow-md backdrop-blur-sm',
      )}
      style={{ left: 'var(--mc-content-left, 16rem)' }}
      data-testid={testId}
      aria-hidden={!visible}
    >
      <div className="px-4 md:px-8 lg:px-12 py-2.5 flex items-center justify-between gap-3">
        <h2
          className="text-sm md:text-base font-semibold tracking-tight truncate"
          data-testid={`${testId}-title`}
        >
          {title}
        </h2>
        {actions && (
          <div
            className="flex items-center gap-2 shrink-0"
            data-testid={`${testId}-actions`}
          >
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

export default StickyHeroStrip;
